import {
  ContentfulContentType,
  ContentfulKey,
  GenericMarketingPage,
  PageSectionKey,
  PageSection,
  PerkDocument,
} from "@tiicker/util/lib/contentful/types";
import React, { useState } from "react";
import { GetStaticProps } from "next";
import { getContentfulClient } from "@tiicker/util/lib/contentful/client";
import { Entry, EntryCollection } from "contentful";
import { compact, uniq } from "lodash";
import {
  ContentSections,
  getImageReference,
  getItemReference,
  loadContentSections,
} from "@tiicker/util/lib/contentful/helpers";
import GenericMetaTags from "components/MetaTags/GenericMetaTags";
import NavigationBar from "components/Global/Navigation/NavigationBar/NavigationBar";
import styles from "./brands.module.scss";
import SpotlightBrandSearch from "components/SpotlightBrandSearch/SpotlightBrandSearch";
import { useFetchUser } from "source/hooks";
import FooterV2 from "components/FooterV2/FooterV2";
import ColorWrapper from "components/Home/ColorWrapper/ColorWrapper";
import BrandCarousel from "components/BrandCarousel/BrandCarousel";
import Container from "components/core/Layout/Container/Container";
import RecommendedBrandsV2 from "components/RecommendedBrands/RecommendedBrandsV2";
import VoteForPerksModal from "components/Perks/VoteForPerkModal/VoteForPerksModal";
import PerksPageCTA from "components/Perks/PerksPageCTA/PerksPageCTA";
import LoginModal from "components/LoginModal/LoginModal";
import NewPerksFromBrands from "components/Perks/NewPerksFromBrands/NewPerksFromBrands";
import { useRecommendations } from "components/RecommendationContext/RecommendationContext";
import { useContentfulEntries } from "source/contentful/provider";
import { MostRecentPerks } from "@tiicker/util/lib/contentful/queries";
import StartEarningPerksMadi from "components/StartEarningPerksMadi/StartEarningPerksMadi";

interface Props {
  pageRecord: Entry<GenericMarketingPage> | null;
  brandSpotlightEntries: EntryCollection<GenericMarketingPage>;
  brandSlider: Entry<PageSection> | undefined;
  brandSearch: Entry<PageSection> | undefined;
  joinNowCTA: Entry<PageSection> | undefined;
  voteCTA: Entry<PageSection> | undefined;
  contentSections: ContentSections;
}

const Brands = (props: Props) => {
  const recommendations = useRecommendations();
  const [votePerkModal, setVotePerkModal] = useState<boolean>(false);
  const { user } = useFetchUser();

  const perks = useContentfulEntries<PerkDocument>(ContentfulContentType.Perk, {
    query: MostRecentPerks(),
  });

  const tickerSymbol =
    perks.state === "DONE"
      ? uniq(perks.result.items.map((x) => x.fields.brand.fields.tickerSymbol))
      : [];

  const removeBrandNameTiiCKER = "TiiCKER";

  const recentBrands =
    perks.state === "DONE"
      ? compact(
          tickerSymbol.map((ticker) => {
            const b = perks.result.items.find(
              (p) => p.fields.brand.fields.tickerSymbol == ticker
            )?.fields.brand.fields;

            if (!b || b.brandName === removeBrandNameTiiCKER) {
              return false;
            }

            const logoImage = getImageReference(
              b.companyLogoDark,
              perks.result.includes
            );
            return {
              tickerSymbol: b.tickerSymbol,
              imageUrl: logoImage?.fields.file.url,
              brandName: b.brandName,
              perkSummary: b.perkSummary,
            };
          })
        )
      : [];
  // const brandSearchQuery = useQueryBundle(GetBrandsFromSearch, { variables: {
  //     perksAvailableOnly: true,
  //     page: 1,
  //     pageSize: 11,
  //     sortBy: "createdAt",
  //     sortByDescending: true,
  //   },
  // });

  const openVotePerkModal = () => {
    setVotePerkModal(true);
  };

  const closeVotePerkModal = () => {
    setVotePerkModal(false);
  };

  const brands = recommendations.useBrands(3);

  return (
    <>
      {props.pageRecord && (
        <GenericMetaTags pageContent={props.pageRecord.fields} />
      )}
      <ColorWrapper>
        <NavigationBar />
        <div className="container">
          {props.pageRecord?.fields.displayTitle && (
            <h1 className={styles.brandsHeading}>
              {props.pageRecord.fields.displayTitle}
            </h1>
          )}
          {props.pageRecord?.fields.displayDescription && (
            <p className={styles.brandsDescription}>
              {props.pageRecord.fields.displayDescription}
            </p>
          )}
        </div>
        {props.brandSearch && (
          <SpotlightBrandSearch
            brandSearch={props.brandSearch}
            content={props.contentSections.sections[props.brandSearch.sys.id]}
            includes={props.brandSpotlightEntries.includes}
          />
        )}
      </ColorWrapper>

      {props.brandSlider && (
        <BrandCarousel
          brandSlider={props.brandSlider}
          content={props.contentSections.sections[props.brandSlider.sys.id]}
          includes={props.brandSpotlightEntries.includes}
        />
      )}

      <div className="container">
        <div className={styles.brandsWithPerks}>
          <NewPerksFromBrands
            title="New Brands With Perks"
            viewAllBrandsCard
            loadingCount={11}
            brands={recentBrands.slice(0, 11).map((brand) => {
              return {
                tickerSymbol: brand.tickerSymbol,
                name: brand.brandName,
                logo: brand.imageUrl ?? "",
                perkSummary: brand.perkSummary ?? undefined,
              };
            })}
          />
        </div>
      </div>

      <Container>
        {brands.length > 0 && (
          <RecommendedBrandsV2
            tickerSymbols={brands.slice(0, 3).map((b) => b.ticker)}
            centeredHeading={true}
          />
        )}
      </Container>

      {props.voteCTA?.fields.displayTitle &&
        props.voteCTA?.fields.description && (
          <PerksPageCTA
            title={props.voteCTA.fields.displayTitle}
            description={props.voteCTA.fields.description}
            onClick={openVotePerkModal}
            buttonText="Vote Now"
          />
        )}

      {props.joinNowCTA && (
        <StartEarningPerksMadi
          page={props.joinNowCTA}
          includes={props.contentSections.includes}
        />
      )}
      {user && props.voteCTA ? (
        <VoteForPerksModal
          visible={votePerkModal}
          onClose={closeVotePerkModal}
          description={props.voteCTA?.fields.description}
          title={props.voteCTA?.fields.displayTitle}
          userId={user.id}
        />
      ) : (
        <LoginModal
          show={votePerkModal}
          showModal={setVotePerkModal}
          description="Please sign in or sign up before voting for perks!"
        />
      )}
      <FooterV2 />
    </>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const client = getContentfulClient("contentful");

  const brandSpotlight = await client.getEntries<GenericMarketingPage>({
    content_type: ContentfulContentType.GenericMarketingPage,
    ["fields.key"]: ContentfulKey.BrandSpotlight,
  });

  const pageRecord =
    brandSpotlight.items.length > 0 ? brandSpotlight.items[0] : null;

  const pageSections = compact(
    pageRecord?.fields.pageSections?.map((section) =>
      getItemReference(section, brandSpotlight.includes)
    )
  );

  const brandSearch = pageSections.find(
    (x) => x.fields.sectionKey === PageSectionKey.HomeBrandSearch
  );

  const brandSlider = pageSections.find(
    (x) => x.fields.sectionKey === PageSectionKey.BrandSpotlightSlider
  );

  const joinNowCTA = pageSections.find(
    (x) => x.fields.sectionKey === PageSectionKey.JoinNowCTA
  );

  const voteCTA = pageSections.find(
    (section) => section.fields.sectionKey === PageSectionKey.PerksVote
  );

  const contentSections = await loadContentSections(client, pageSections);

  return {
    props: {
      pageRecord,
      brandSpotlightEntries: brandSpotlight,
      brandSlider,
      joinNowCTA,
      voteCTA,
      brandSearch,
      contentSections,
    },
    revalidate: 100,
  };
};

export default Brands;
